// ui/recap-text.js: restyle step 3B, pure helpers for the recap panel:
// the tricks each team took (the score blocks' marks), the summary
// sentence under them and the rows of the TRICK HISTORY list.
//
// Kept apart from EndOfHandModal.jsx so Node tests can import it.

/** team_a = seats 1 and 3, team_b = seats 2 and 4. */
function teamOfSeat(seat) {
  return seat === 1 || seat === 3 ? 'team_a' : 'team_b';
}

/** A Lua array arrives as an array or as an object with numeric keys. */
function asList(value) {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return Object.values(value);
  return [];
}

/**
 * Tricks won this hand by the viewer's team (US) and the other (THEM).
 *
 * @param {any} tricks  completed tricks, each with winner_seat
 * @param {'team_a'|'team_b'} myTeam
 * @returns {{US: number, THEM: number}}
 */
export function tricksWonByTeam(tricks, myTeam) {
  const won = { US: 0, THEM: 0 };
  for (const t of asList(tricks)) {
    const seat = Number(t?.winner_seat);
    if (!Number.isInteger(seat) || seat < 1 || seat > 4) continue;
    won[teamOfSeat(seat) === myTeam ? 'US' : 'THEM'] += 1;
  }
  return won;
}

/** "1 trick" or "3 tricks". */
function tricksWord(n) {
  return n === 1 ? '1 trick' : `${n} tricks`;
}

/**
 * The sentence under the score blocks: who called which suit and how the
 * hand went, then the trick counts. Without the maker's name, team or
 * the suit it is the trick counts alone.
 *
 * @param {{
 *   summary: { result?: string, was_loner?: boolean },
 *   myTeam: 'team_a'|'team_b',
 *   makerTeam: 'team_a'|'team_b'|null,
 *   makerName: string,
 *   trumpWord: string,
 *   won: {US: number, THEM: number},
 * }} args  makerName is the plain seat name ("You", "Bot Hal"); trumpWord
 *   is the suit in running text ("hearts").
 * @returns {string}
 */
export function recapSentence({ summary, myTeam, makerTeam, makerName, trumpWord, won }) {
  const counts = `Tricks: us ${won.US}, them ${won.THEM}.`;
  if (!makerName || !trumpWord || !makerTeam) return counts;
  const makerIsUs = makerTeam === myTeam;
  const took = makerIsUs ? won.US : won.THEM;
  if (summary?.result === 'euchred') {
    const stopped = makerIsUs ? 'They stopped us.' : 'You stopped them.';
    return `${makerName} called ${trumpWord} but took only ${took}. ${stopped} ${counts}`;
  }
  const verb = summary?.was_loner ? `went alone on ${trumpWord}` : `called ${trumpWord}`;
  const taken = took === 5 ? 'all 5 tricks' : tricksWord(took);
  return `${makerName} ${verb} and took ${taken}. ${counts}`;
}

/**
 * The TRICK HISTORY rows: one per finished trick that has its cards, in
 * play order (the first card is the leader's).
 *
 * @param {any} tricks
 * @param {'team_a'|'team_b'} myTeam
 * @returns {Array<{n: number, leader: number, winner: number, winTeam: 'US'|'THEM', plays: Array<{seat: number, card_id: string}>}>}
 */
export function historyRows(tricks, myTeam) {
  /** @type {Array<any>} */
  const rows = [];
  asList(tricks).forEach((t, i) => {
    const plays = asList(t?.plays)
      .map((p) => ({ seat: Number(p?.seat), card_id: String(p?.card_id) }))
      .filter((p) => Number.isInteger(p.seat) && p.card_id !== 'undefined');
    const winner = Number(t?.winner_seat);
    if (!plays.length || !Number.isInteger(winner)) return;
    rows.push({ n: i + 1, leader: plays[0].seat, winner, winTeam: teamOfSeat(winner) === myTeam ? 'US' : 'THEM', plays });
  });
  return rows;
}
